import React, { useRef, useState } from 'react';
import ClipLoader from 'react-spinners/ClipLoader';
import Icon from '@mdi/react';
import {
  mdiAlertOutline,
  mdiCheck,
  mdiClipboardCheckOutline,
  mdiFileDocumentOutline,
  mdiImageOutline,
  mdiArrowDown,
  mdiWrenchOutline,
} from '@mdi/js';
import CTAButton from '../../shared/CTAButton';
import { concernLevelColorMap } from '../../VisualAssets/helperConstants';
import errorIcon from '../../../assets/images/error.svg';
import './tryItOut.css';

type LevelKey = '0' | '1' | '2';

interface LevelDef {
  key: LevelKey;
  label: string;
  resultLabel: string;
  value: number;
  resultColor: string;
  accent: string;
  iconPath: string;
}

const LEVELS: LevelDef[] = [
  {
    key: '0',
    label: 'Maintenance',
    resultLabel: 'Maintenance Item',
    value: 0,
    resultColor: concernLevelColorMap['concern-maintenance'],
    accent: '#005881',
    iconPath: mdiWrenchOutline,
  },
  {
    key: '1',
    label: 'Recommendation',
    resultLabel: 'Recommendation',
    value: 1,
    resultColor: concernLevelColorMap['concern-recommendation'],
    accent: '#D4A012',
    iconPath: mdiClipboardCheckOutline,
  },
  {
    key: '2',
    label: 'Safety Hazard',
    resultLabel: 'Safety Hazard',
    value: 2,
    resultColor: concernLevelColorMap['concern-hazard'],
    accent: '#B12E00',
    iconPath: mdiAlertOutline,
  },
];

interface Code {
  code: string;
  section: string;
  content?: string;
  explanation?: string;
  relevance?: number;
  source_url?: string;
  code_id?: string;
  chapter_title?: string;
  jurisdiction?: string;
}

interface AnalysisData {
  description: string;
  cause: string;
  solution: string;
  name: string;
  section: string;
  subsection: string;
  code_1: Code | null;
  code_2: Code | null;
  code_3: Code | null;
  concern_level: number;
}

interface TryItOutProps {
  setToastMessage: (message: string) => void;
  setShowToast: (showToast: boolean) => void;
  openWaitlist?: () => void;
}

const TryItOut: React.FC<TryItOutProps> = ({
  setToastMessage,
  setShowToast,
  openWaitlist,
}) => {
  const [imageFiles, setImageFiles] = useState<File[]>([]);
  const [previewUrls, setPreviewUrls] = useState<string[]>([]);
  const [level, setLevel] = useState<LevelKey | ''>('');
  const [briefNote, setBriefNote] = useState('');
  const [loading, setLoading] = useState(false);
  const [analysis, setAnalysis] = useState<AnalysisData | null>(null);
  const [error, setError] = useState(false);
  const [showWaitlistPrompt, setShowWaitlistPrompt] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const addFiles = (files: FileList | File[]) => {
    setShowToast(false);
    const incoming = Array.from(files).filter((f) =>
      f.type.startsWith('image/'),
    );
    if (imageFiles.length + incoming.length > 10) {
      setToastMessage('Maximum 10 photos allowed.');
      setShowToast(true);
      return;
    }
    if (incoming.length === 0) return;
    setImageFiles((prev) => [...prev, ...incoming]);
    setPreviewUrls((prev) => [
      ...prev,
      ...incoming.map((f) => URL.createObjectURL(f)),
    ]);
  };

  const handleBrowse = () => fileInputRef.current?.click();

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) addFiles(e.target.files);
    e.target.value = '';
  };

  const handleDragOver = (e: React.DragEvent) => e.preventDefault();
  const handleDragEnter = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.currentTarget.classList.add('is-dragging');
  };
  const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.currentTarget.classList.remove('is-dragging');
  };
  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.currentTarget.classList.remove('is-dragging');
    if (e.dataTransfer.files.length) addFiles(e.dataTransfer.files);
  };

  const removePhoto = (index: number) => {
    URL.revokeObjectURL(previewUrls[index]);
    setImageFiles((prev) => prev.filter((_, i) => i !== index));
    setPreviewUrls((prev) => prev.filter((_, i) => i !== index));
  };

  const resetAll = () => {
    previewUrls.forEach((url) => URL.revokeObjectURL(url));
    setImageFiles([]);
    setPreviewUrls([]);
    setLevel('');
    setBriefNote('');
    setAnalysis(null);
    setError(false);
    setShowWaitlistPrompt(false);
    setLoading(false);
  };

  const handleAnalyze = async () => {
    if (imageFiles.length === 0) {
      setToastMessage('Upload at least one photo to start analysis.');
      setShowToast(true);
      return;
    }
    if (level === '') {
      setToastMessage('Select a concerning level to start analysis.');
      setShowToast(true);
      return;
    }
    setLoading(true);
    setError(false);

    try {
      const base64Images = await Promise.all(
        imageFiles.map(
          (file) =>
            new Promise<string>((resolve, reject) => {
              const reader = new FileReader();
              reader.onload = () => {
                const dataUrl = reader.result as string;
                resolve(dataUrl.split(',')[1]);
              };
              reader.onerror = reject;
              reader.readAsDataURL(file);
            }),
        ),
      );

      const payload = {
        images: base64Images,
        concern_level: LEVELS.find((l) => l.key === level)!.value,
        description_hint: briefNote,
      };

      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/issues/generate/anonymous/`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
        },
      );

      if (!response.ok) {
        if (response.status === 429) {
          setShowWaitlistPrompt(true);
          return;
        }
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = (await response.json()) as AnalysisData;
      setAnalysis(data);
    } catch (err) {
      console.error('AI analyze failed:', err);
      setError(true);
    } finally {
      setLoading(false);
    }
  };

  const isComplete = !loading && !!analysis && !error && !showWaitlistPrompt;
  const SAMPLE_REPORT_URL =
    'https://app.morph-technologies.com/compiled-reports/141d7cd1-11ae-4f79-8986-b1c3c009e376';

  return (
    <section className="v2-playground" id="playground" data-page-bg="#0a1628">
      <div className="v2-playground__container">
        <div className="v2-playground__invite">
          <span className="v2-playground__invite-label">Try it yourself</span>
          <span className="v2-playground__invite-arrow" aria-hidden="true">
            <Icon path={mdiArrowDown} size={0.6} />
          </span>
        </div>

        <h2 className="ui-h2 v2-playground__heading">
          See Morph analyze a home inspection photo
        </h2>

        <div className="v2-playground__grid">
          {/* Left: input panel */}
          <div className="pg-input">
            <div
              className="pg-upload"
              onDragOver={handleDragOver}
              onDragEnter={handleDragEnter}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
              onClick={handleBrowse}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => e.key === 'Enter' && handleBrowse()}
            >
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                multiple
                onChange={handleFileChange}
                style={{ display: 'none' }}
              />
              {imageFiles.length === 0 ? (
                <>
                  <span className="pg-upload__icon" aria-hidden="true">
                    <Icon path={mdiImageOutline} size={1.4} />
                  </span>
                  <div className="pg-upload__row">
                    <span className="pg-upload__label">
                      Drag &amp; drop photos
                    </span>
                    <span className="pg-upload__max">Max 10</span>
                  </div>
                  <div className="pg-upload__row pg-upload__row--secondary">
                    <span className="pg-upload__or">or</span>
                    <CTAButton
                      variant="outline-light"
                      size="sm"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleBrowse();
                      }}
                    >
                      Browse to Upload
                    </CTAButton>
                  </div>
                </>
              ) : (
                <div className="pg-photos">
                  {previewUrls.map((url, i) => (
                    <div
                      key={url}
                      className="pg-photo"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <img src={url} alt={`Upload ${i + 1}`} />
                      <button
                        type="button"
                        className="pg-photo__remove"
                        onClick={(e) => {
                          e.stopPropagation();
                          removePhoto(i);
                        }}
                        aria-label={`Remove photo ${i + 1}`}
                      >
                        ×
                      </button>
                    </div>
                  ))}
                  {imageFiles.length < 10 && (
                    <div className="pg-photo pg-photo--add" aria-hidden="true">
                      +
                    </div>
                  )}
                </div>
              )}
            </div>

            <div className="pg-level-cards">
              {LEVELS.map(({ key, label, iconPath, accent }) => (
                <button
                  key={key}
                  type="button"
                  className={`pg-level ${level === key ? 'is-selected' : ''}`}
                  style={
                    {
                      '--level-accent': accent,
                    } as React.CSSProperties
                  }
                  onClick={() => setLevel(key)}
                  aria-pressed={level === key}
                >
                  <span className="pg-level__icon">
                    <Icon path={iconPath} size={0.95} />
                  </span>
                  <span className="pg-level__name">{label}</span>
                  {level === key && (
                    <span className="pg-level__check" aria-hidden="true">
                      <Icon path={mdiCheck} size={0.55} />
                    </span>
                  )}
                </button>
              ))}
            </div>

            <textarea
              className="pg-notes"
              placeholder="Short note for more precise analysis (Optional)"
              value={briefNote}
              onChange={(e) => setBriefNote(e.target.value)}
            />

            <CTAButton
              variant="blue"
              size="lg"
              onClick={isComplete ? resetAll : handleAnalyze}
              disabled={loading}
              className="pg-analyze"
            >
              {loading ? 'Analyzing…' : isComplete ? 'Retry' : 'AI Analyze'}
            </CTAButton>
          </div>

          {/* Right: output panel */}
          <div className="pg-output">
            <div className="pg-output__label">AI output</div>

            {loading && (
              <div className="pg-output__body pg-output__center">
                <ClipLoader color="#075AD8" size={48} />
                <p className="pg-output__loading">Analyzing…</p>
              </div>
            )}

            {!loading && error && (
              <div className="pg-output__body pg-output__center">
                <img src={errorIcon} alt="" width={40} height={40} />
                <p className="pg-output__error-title">
                  Something went wrong. Please try again.
                </p>
                <p className="pg-output__error-copy">
                  If the issue happens again, please contact us for help.
                </p>
                <div className="pg-output__actions">
                  <CTAButton
                    as="a"
                    variant="outline-light"
                    href="mailto:help@morph-technologies.com"
                  >
                    Contact
                  </CTAButton>
                  <CTAButton variant="blue" onClick={resetAll}>
                    Try Again
                  </CTAButton>
                </div>
              </div>
            )}

            {!loading && !error && showWaitlistPrompt && (
              <div className="pg-output__body pg-output__center">
                <p className="pg-output__error-title">
                  Glad you&apos;re enjoying the demo!
                </p>
                <p className="pg-output__error-copy">
                  You&apos;ve hit the public limit. Join the waitlist for
                  unlimited access.
                </p>
                <div className="pg-output__actions">
                  <CTAButton variant="blue" onClick={openWaitlist}>
                    Join Waitlist
                  </CTAButton>
                </div>
              </div>
            )}

            {isComplete && analysis && (
              <div className="pg-output__body pg-output__body--result">
                <div className="pg-output__scroll">
                  <div className="pg-output__meta">
                    <div className="pg-output__title">{analysis.name}</div>
                    <div className="pg-output__sub">
                      {analysis.section}
                      {analysis.subsection ? ` / ${analysis.subsection}` : ''}
                    </div>
                  </div>
                  {analysis.description && (
                    <div className="pg-output__sec">
                      <h4 className="pg-output__sec-title">Description</h4>
                      <p>{analysis.description}</p>
                    </div>
                  )}
                  {analysis.cause && (
                    <div className="pg-output__sec">
                      <h4 className="pg-output__sec-title">Cause</h4>
                      <p>{analysis.cause}</p>
                    </div>
                  )}
                  {analysis.solution && (
                    <div className="pg-output__sec">
                      <h4 className="pg-output__sec-title">Solution</h4>
                      <p>{analysis.solution}</p>
                    </div>
                  )}
                  {(() => {
                    const codes = [
                      analysis.code_1,
                      analysis.code_2,
                      analysis.code_3,
                    ].filter((c): c is Code => Boolean(c));
                    if (codes.length === 0) return null;
                    return (
                      <div className="pg-output__sec">
                        <h4 className="pg-output__sec-title">
                          {codes.length > 1
                            ? 'Code References'
                            : 'Code Reference'}
                        </h4>
                        <div className="pg-output__codes">
                          {codes.map((code, i) => {
                            const meta = [code.chapter_title, code.jurisdiction]
                              .filter(Boolean)
                              .join(' · ');
                            return (
                              <div key={i} className="pg-output__code">
                                <div className="pg-output__code-head">
                                  <div className="pg-output__code-name">
                                    {code.code}
                                    {code.section ? ` § ${code.section}` : ''}
                                  </div>
                                  {typeof code.relevance === 'number' && (
                                    <span className="pg-output__code-relevance">
                                      {code.relevance}% match
                                    </span>
                                  )}
                                </div>
                                {meta && (
                                  <div className="pg-output__code-meta">
                                    {meta}
                                  </div>
                                )}
                                {code.explanation && (
                                  <p className="pg-output__code-explanation">
                                    {code.explanation}
                                  </p>
                                )}
                                {code.source_url && (
                                  <a
                                    href={code.source_url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="pg-output__code-link"
                                  >
                                    View source →
                                  </a>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    );
                  })()}
                </div>
                <div className="pg-output__footer">
                  <a
                    className="pg-output__sample"
                    href={SAMPLE_REPORT_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    View Sample Report →
                  </a>
                  <div className="pg-output__cta">
                    <p>
                      Imagine every item in your report written on the spot.
                    </p>
                    <CTAButton
                      variant="gold"
                      onClick={() =>
                        document
                          .getElementById('download')
                          ?.scrollIntoView({ behavior: 'smooth' })
                      }
                    >
                      Start Free Trial
                    </CTAButton>
                  </div>
                </div>
              </div>
            )}

            {!loading && !error && !showWaitlistPrompt && !analysis && (
              <div className="pg-output__body pg-output__center">
                <div className="pg-output__empty-icon">
                  <Icon path={mdiFileDocumentOutline} size={1.6} />
                </div>
                <ol className="pg-output__steps">
                  <li>Upload photos</li>
                  <li>Select a concerning level</li>
                  <li>Tap AI Analyze</li>
                </ol>
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default TryItOut;
